import { Injectable } from '@angular/core';
import { ProblemReportScreenshot } from './problem-report.service';

export interface ProblemReportContext {
  currentUrl: string;
  sectorId: number | null;
  routeId: number | null;
  routeDate: string | null;
}

interface ProblemReportDraft {
  description: string;
  screenshot?: ProblemReportScreenshot;
  context?: ProblemReportContext;
  userName?: string;
  savedAt: number;
}

const STORAGE_KEY = 'tpslRouteProblemReportDraft';
const SCREENSHOT_STORAGE_KEY = 'tpslRouteProblemReportScreenshot';
const DRAFT_LIFETIME_MS = 24 * 60 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class ProblemReportDraftService {
  private draft: ProblemReportDraft;

  get(): ProblemReportDraft | null {
    if (!this.draft) {
      try {
        this.draft = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
      } catch (_) {
        this.clear();
        return null;
      }
      if (this.draft) {
        try { this.draft.screenshot = JSON.parse(sessionStorage.getItem(SCREENSHOT_STORAGE_KEY)); }
        catch (_) { this.draft.screenshot = undefined; }
      }
    }
    if (!this.draft || typeof this.draft.description !== 'string' ||
        !this.draft.savedAt || Date.now() - this.draft.savedAt > DRAFT_LIFETIME_MS) {
      this.clear();
      return null;
    }
    const currentUserName = this.currentUserName();
    if (this.draft.userName && currentUserName && this.draft.userName.toLowerCase() !== currentUserName.toLowerCase()) {
      this.clear();
      return null;
    }
    return this.draft;
  }

  save(description: string, screenshot?: ProblemReportScreenshot, context?: ProblemReportContext): void {
    const previousScreenshot = this.draft && this.draft.screenshot;
    this.draft = { description, screenshot, context, userName: this.currentUserName(), savedAt: Date.now() };
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ description, context, userName: this.draft.userName, savedAt: this.draft.savedAt }));
      if (!screenshot) { sessionStorage.removeItem(SCREENSHOT_STORAGE_KEY); }
      else if (screenshot !== previousScreenshot) {
        sessionStorage.setItem(SCREENSHOT_STORAGE_KEY, JSON.stringify(screenshot));
      }
    } catch (_) { /* The in-memory draft still survives the login redirect. */ }
  }

  clear(): void {
    this.draft = undefined;
    try {
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(SCREENSHOT_STORAGE_KEY);
    } catch (_) { /* Storage can be disabled. */ }
  }

  private currentUserName(): string | null {
    try {
      const user = JSON.parse(localStorage.getItem('currentUser'));
      return user && user.UserName || null;
    } catch (_) { return null; }
  }
}
